/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import CaptureHeader from '../CaptureHeader';
import GlobalInstructionButton from './GlobalInstructionButton';
import Modal from '../Modal';
import { ReviewItem, FullScreenMediaSlide } from './Review';
import ReferenceTooltip from './ReferenceTooltip';
import FullScreenGallery from './FullScreenGallery';
import NotesButton from './NotesButton';
import NotesSummary from './NotesSummary';
import SyncStatusToast from '../SyncStatusToast';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../../utils/customerReferenceLabel';
import './capture.css';

const SYNC_BLOCKED_MESSAGE = 'Before proceeding, you need to sync the data.';

// Shared read-only "review" shell used by ResumeGate and SavedExit — the
// same header/tooltip/gallery/item-list/sticky-bar layout as Review.jsx's
// submit screen, parameterized by intro copy, the single primary action,
// and optional extra content (e.g. SavedExit's note-confirmation modals).
// Items are read-only by default (ResumeGate never passes editableItemIds/
// the on* handlers, so it stays a pure preview). A caller can opt specific
// items into Retake/Delete/Add-photo by passing editableItemIds — SavedExit
// uses this to only allow editing media captured during the current session.
const ReviewShell = ({
  items,
  capturedFiles,
  serverCompleted,
  completedItemsMedia,
  assignment,
  workflow,
  branding,
  heading,
  body,
  error = null,
  onNotesClick,
  notes = [],
  onAddNote,
  showNotesWarning = true,
  actionLabel,
  onAction,
  actionDisabled = false,
  editableItemIds = null,
  onRetake,
  onDelete,
  onDeletePhoto,
  onAddPhoto,
  token,
  syncBlockedNotice = false,
  onDismissSyncBlockedNotice,
  syncStatus = null,
  onDismissSyncStatus,
  children,
}) => {
  const [viewer, setViewer] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, itemId: null, itemDescription: '' });
  const tooltipTriggerRef = useRef(null);
  const nameTooltipTriggerRef = useRef(null);

  const currentTheme = branding?.theme || 'light';
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  // Scroll to top when an action error appears so the user sees the error banner immediately
  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  return (
    <div data-theme={currentTheme} className="min-h-screen flex flex-col bg-base-100 text-base-content overflow-x-hidden capture-container landscape-root">
      {viewer && (
        <FullScreenGallery
          items={viewer.items}
          initialIndex={viewer.initialIndex}
          onClose={() => setViewer(null)}
          onIndexChange={viewer.onIndexChange}
          renderItem={(slide) => (
            <FullScreenMediaSlide
              key={slide.url}
              url={slide.url}
              type={viewer.type}
              mimeType={slide.mimeType}
              fileName={slide.fileName}
              token={token}
            />
          )}
        />
      )}

      {/* ── LEFT SIDEBAR ── */}
      <div className="landscape-sidebar landscape-review-sidebar w-full">
        <div className="h-[env(safe-area-inset-top,20px)]" />

        <div className="w-full bg-base-100/95 backdrop-blur-sm p-3 pt-0">
          <div className="w-full max-w-full mx-auto text-left">
            <CaptureHeader expiresAt={assignment?.expiresAt} brandLogo={workflow?.branding?.logoUrl} branding={branding} className="pt-0 pb-0" type='after-capture' />

            <div className="flex flex-col gap-1 mt-1">
              <div className="flex justify-between items-start gap-2">
                <div className="relative min-w-0">
                  <h2
                    ref={nameTooltipTriggerRef}
                    className="text-[20px] font-bold text-base-content leading-none tracking-tight truncate cursor-pointer transition-opacity hover:opacity-80 active:opacity-70"
                    onClick={() => setShowNameTooltip(!showNameTooltip)}
                  >
                    {assignment?.submitterName || assignment?.workflowName || 'Mobile Capture'}
                  </h2>
                  <ReferenceTooltip
                    assignment={assignment}
                    workflow={workflow}
                    show={showNameTooltip}
                    onClose={() => setShowNameTooltip(false)}
                    theme={currentTheme}
                    triggerRef={nameTooltipTriggerRef}
                    type="name"
                    className="tooltip-for-landscap-review"
                  />
                </div>
                {onNotesClick && <NotesButton onClick={onNotesClick} />}
              </div>
              <div className="shrink max-w-full overflow-visible text-[14px]">
                <div className="relative flex flex-wrap justify-between items-start custom-line-break w-full">
                  <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:{" "}</span>
                  <span className="inline-block text-right shrink max-w-full break-all">
                    <button
                      type="button"
                      ref={tooltipTriggerRef}
                      onClick={() => setShowTooltip(!showTooltip)}
                      className="focus:outline-none underline decoration-dashed text-primary custom-line-break"
                    >
                      {assignment?.customerReferenceNumber}
                    </button>
                  </span>
                  <ReferenceTooltip
                    assignment={assignment}
                    workflow={workflow}
                    show={showTooltip}
                    onClose={() => setShowTooltip(false)}
                    theme={currentTheme}
                    triggerRef={tooltipTriggerRef}
                    type="reference"
                    className="tooltip-for-landscap-review"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 p-3 w-full max-w-md mx-auto landscape-portrait-review-intro">
          <div>
            <h1 className="text-[20px] font-bold text-base-content mb-2 tracking-tight">{heading}</h1>
            <p className="text-[14px] leading-tight text-base-content">{body}</p>
          </div>

          {error && (
            <div className="mt-3 p-4 bg-error/10 border border-error/20 rounded-xl text-[13px] font-bold text-error flex items-center gap-3">
              <div className="w-2 h-2 bg-error rounded-full shrink-0" />
              {error}
            </div>
          )}
        </div>
        <div className="landscape-sidebar-action hidden flex-col p-3">
          <h1 className="text-[18px] font-bold text-base-content mb-1 tracking-tight">{heading}</h1>
          <p className="text-[13px] leading-tight text-base-content/70">{body}</p>
        </div>
        <div className="landscape-sidebar-action hidden flex-col gap-3 p-3">
          {error && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-xl text-[12px] font-bold text-error">
              {error}
            </div>
          )}
          {syncBlockedNotice && (
            <SyncStatusToast
              status="error"
              message={SYNC_BLOCKED_MESSAGE}
              onDismiss={onDismissSyncBlockedNotice}
              variant="inline"
            />
          )}
          {syncStatus?.status && (
            <SyncStatusToast
              status={syncStatus.status}
              message={syncStatus.message}
              onDismiss={onDismissSyncStatus}
              variant="inline"
            />
          )}
          <div className="flex items-center gap-3">
            <GlobalInstructionButton globalInstructionUrl={workflow?.globalInstructionUrl} token={token} className="!w-11 !h-11 shrink-0" />
            <button
              onClick={onAction}
              disabled={actionDisabled}
              className="flex-1 py-2.5 rounded-xl font-semibold text-[17px] transition-all active:scale-[0.98] shadow-xl bg-primary text-primary-content disabled:opacity-60"
            >
              {actionLabel}
            </button>
          </div>
        </div>
      </div>

      {/* ── RIGHT MAIN PANEL / Portrait full-scroll area ── */}
      <div className="landscape-main flex-1">
        <div className="p-4 space-y-6 w-full max-w-full mx-auto text-left pb-[140px] landscape-pb-0">
          {onNotesClick && <NotesSummary notes={notes} onView={onNotesClick} onAdd={onAddNote} showWarning={showNotesWarning} />}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {items.map((item, index) => (
              <div key={item.id}>
                <ReviewItem
                  item={item}
                  index={index}
                  capturedFiles={capturedFiles}
                  serverCompleted={serverCompleted}
                  completedItemsMedia={completedItemsMedia}
                  onRetake={onRetake}
                  onDeletePhoto={onDeletePhoto}
                  onAddPhoto={onAddPhoto}
                  setViewer={setViewer}
                  setDeleteModal={setDeleteModal}
                  readOnly={!editableItemIds?.has(item.assignmentItemId)}
                  token={token}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Portrait: sticky bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 p-4 px-3 bg-base-100/95 backdrop-blur-sm rounded-t-[20px] z-30 shadow-[0_-4px_10px_rgba(0,0,0,0.1)] border-t border-base-300/50 landscape-hide-fixed-bar">
        <div className="max-w-full mx-auto flex flex-col justify-center gap-10">
          {syncBlockedNotice && (
            <SyncStatusToast
              status="error"
              message={SYNC_BLOCKED_MESSAGE}
              onDismiss={onDismissSyncBlockedNotice}
              variant="inline"
            />
          )}
          {syncStatus?.status && (
            <SyncStatusToast
              status={syncStatus.status}
              message={syncStatus.message}
              onDismiss={onDismissSyncStatus}
              variant="inline"
            />
          )}
          <div className="flex items-center justify-center gap-10">
            <GlobalInstructionButton globalInstructionUrl={workflow?.globalInstructionUrl} token={token} className="!w-11 !h-11 shrink-0" />
            <button
              onClick={onAction}
              disabled={actionDisabled}
              className="py-2 px-10 rounded-xl font-semibold text-base transition-all active:scale-[0.98] shadow-xl bg-primary text-primary-content disabled:opacity-60"
            >
              {actionLabel}
            </button>
          </div>
        </div>
      </div>

      <div className="h-[env(safe-area-inset-bottom,20px)] bg-base-100" />

      {/* Delete Confirmation Modal — inert unless a caller wires up onDelete
          and marks an item editable via editableItemIds (see ReviewItem). */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, itemId: null, itemDescription: '' })}
        title="Delete Media"
        fontFamily="Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', Oxygen, sans-serif"
        theme={currentTheme}
      >
        <div className="space-y-4">
          <p className="text-base-content/60 text-[15px]">
            Are you sure you want to delete this media?
          </p>
          <p className="text-base-content font-semibold text-[14px]">
            {deleteModal.itemDescription}
          </p>
          <p className="text-base-content/50 text-[13px]">
            This action cannot be undone.
          </p>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setDeleteModal({ isOpen: false, itemId: null, itemDescription: '' })}
              className="flex-1 py-3 px-4 bg-base-200 text-base-content font-semibold text-[15px] rounded-xl hover:bg-base-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onDelete?.(deleteModal.itemId);
                setDeleteModal({ isOpen: false, itemId: null, itemDescription: '' });
              }}
              className="flex-1 py-3 px-4 bg-error text-error-content font-semibold text-[15px] rounded-xl hover:bg-error/90 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>

      {children}
    </div>
  );
};

export default ReviewShell;

ReviewShell.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      description: PropTypes.string,
      required: PropTypes.bool,
      status: PropTypes.string,
    })
  ),
  capturedFiles: PropTypes.object,
  serverCompleted: PropTypes.object,
  completedItemsMedia: PropTypes.object,
  assignment: PropTypes.shape({
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    workflowName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
  }),
  workflow: PropTypes.shape({
    branding: PropTypes.shape({
      logoUrl: PropTypes.string,
    }),
  }),
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  heading: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  error: PropTypes.string,
  onNotesClick: PropTypes.func,
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      note: PropTypes.string,
      timestamp: PropTypes.string,
    })
  ),
  onAddNote: PropTypes.func,
  showNotesWarning: PropTypes.bool,
  actionLabel: PropTypes.string.isRequired,
  onAction: PropTypes.func.isRequired,
  actionDisabled: PropTypes.bool,
  editableItemIds: PropTypes.instanceOf(Set),
  onRetake: PropTypes.func,
  onDelete: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onAddPhoto: PropTypes.func,
  token: PropTypes.string,
  syncBlockedNotice: PropTypes.bool,
  onDismissSyncBlockedNotice: PropTypes.func,
  syncStatus: PropTypes.shape({
    status: PropTypes.oneOf(['success', 'error']),
    message: PropTypes.string,
  }),
  onDismissSyncStatus: PropTypes.func,
  children: PropTypes.node,
};
